import { Button, Icon, TextButton, Trigger, isSuccess, useLaravelCrudEntity, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { contactEmailService } from "../../shared/api/contactEmailService";
import { contactService } from "../../shared/api/contactService";
import { IContactEmailFormState, IContactUpdateResource } from "../../shared/types";
import {
	MarkAsPrimaryButton,
	PrimaryLabel,
	ViewPageSection,
	ViewPageSectionBody,
	ViewPageSectionHeader,
	ViewPageSectionItem,
	ViewPageSectionListItem,
} from "../../shared/view";
import { DrawerFormButtonContainer, ValueWrapper } from "../../shared/view/ViewComponents";
import { ContactEmailDrawer } from "./ContactEmailDrawer";

const StyledViewPageSection = styled(ViewPageSection)`
	grid-area: email;
`;

export const ContactViewEmailAddressSection: FC<{ contact: IContactUpdateResource }> = ({ contact }) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const crudOperations = useLaravelCrudEntity(contactEmailService, {
		entityName: t("Contact email"),
		messages: {
			create: t("Contact email was saved."),
			update: t("Contact email was saved."),
			delete: t("Contact email was deleted."),
		},
	});
	const emptyContactEmail: IContactEmailFormState = {
		type: "work",
		email: "",
		isPrimary: false,
	};
	const onClosed = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: contactService.listKey });
		queryClient.invalidateQueries({ queryKey: contactService.showKey(contact.id) });
	}, [contact.id, queryClient]);

	return (
		<StyledViewPageSection>
			<ViewPageSectionHeader>{t("Email addresses")}</ViewPageSectionHeader>
			<ViewPageSectionBody>
				{contact.contactEmails.map((x) => (
					<Trigger
						key={x.id}
						render={(props) => (
							<ContactEmailDrawer
								open={props.open}
								onClose={props.handleClose}
								title={t("Edit contact email")}
								initialValues={x}
								onClosed={onClosed}
								hasUser={contact.hasUser}
								onSubmit={(values) =>
									crudOperations.updateEntity(x.id, {
										...values,
										contactId: contact.id,
									})
								}
							>
								{!x.isPrimary && (
									<DrawerFormButtonContainer>
										{!contact.hasUser && (
											<MarkAsPrimaryButton
												entityId={x.id}
												markAsPrimary={contactEmailService.markAsPrimary}
												onSubmit={props.handleClose}
											/>
										)}
										<Button
											type="button"
											color="danger"
											onClick={async () => {
												const res = await contactService.deleteEmailAddress(contact.id, x.id);
												if (isSuccess(res)) {
													showToast({
														body: t("Email address was deleted."),
														title: t("Company"),
														type: "success",
													});
													props.handleClose();
												}
											}}
										>
											{t("Delete email address")}
										</Button>
									</DrawerFormButtonContainer>
								)}
							</ContactEmailDrawer>
						)}
					>
						{(props) => (
							<ViewPageSectionListItem {...props}>
								<ValueWrapper>
									<span>{x.email}</span>
									{x.isPrimary && <PrimaryLabel />}
								</ValueWrapper>
							</ViewPageSectionListItem>
						)}
					</Trigger>
				))}
				<ViewPageSectionItem>
					<Trigger
						render={(props) => (
							<ContactEmailDrawer
								open={props.open}
								onClose={props.handleClose}
								title={t("Add email address")}
								initialValues={emptyContactEmail}
								onClosed={onClosed}
								hasUser={false}
								onSubmit={(values) =>
									crudOperations.createEntity({
										...values,
										contactId: contact.id,
									})
								}
							/>
						)}
					>
						{(props) => (
							<TextButton {...props} color="primary" prefix={<Icon $icon="plus" />}>
								{t("Add email address")}
							</TextButton>
						)}
					</Trigger>
				</ViewPageSectionItem>
			</ViewPageSectionBody>
		</StyledViewPageSection>
	);
};
